import { addDays, format, startOfDay } from 'date-fns';
import type { CalendarAbsence } from '@/hooks/useBookings';

/** Translation key per absence type — the label is resolved in the component. */
const ABSENCE_LABEL_KEYS: Record<CalendarAbsence['type'], string> = {
  sick_leave: 'absence.sickLeave',
  vacation: 'absence.vacation',
};

/** One mechanic's whole-day absence, as the calendar draws it. */
export interface AbsenceBlock {
  id: string;
  mechanicId: string;
  labelKey: string;
  scheduledStart: string;
  scheduledEnd: string;
}

/**
 * Whole visible days a shown mechanic is on sick leave or vacation (#2261).
 *
 * A shift and an absence cannot both hold, so the shift API returns nothing
 * for these days — this block is what tells the planner why the row is empty.
 * Absences are stored as UTC midnight of the day the HR API was given, so the
 * date part of the timestamp is the day they cover.
 */
export function buildAbsenceBlocks({
  start, end, mechanicIds, absences,
}: {
  start: Date;
  end: Date;
  mechanicIds: string[];
  absences: CalendarAbsence[];
}): AbsenceBlock[] {
  const shown = new Set(mechanicIds);
  const blocks: AbsenceBlock[] = [];
  for (let day = startOfDay(start); day < end; day = addDays(day, 1)) {
    const date = format(day, 'yyyy-MM-dd');
    const next = addDays(day, 1);
    for (const absence of absences) {
      if (!shown.has(absence.employeeId)) continue;
      if (absence.startDate.slice(0, 10) > date) continue;
      if (absence.endDate !== null && absence.endDate.slice(0, 10) < date) continue;
      blocks.push({
        id: `${absence.employeeId}-${absence.type}-${date}`,
        mechanicId: absence.employeeId,
        labelKey: ABSENCE_LABEL_KEYS[absence.type] ?? ABSENCE_LABEL_KEYS.vacation,
        scheduledStart: day.toISOString(),
        scheduledEnd: next.toISOString(),
      });
    }
  }
  return blocks;
}
